import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuftragCtx } from './AuftragLayout.jsx';
import {
  gueltigeLeistungsart, useArtikelKatalog, useArtikelSuche, useCreateLeistung, useKatalogAktualisieren, useFavoriten, useLeistungen, useLeistungsarten, useMaterial, useMe, useMeineStunden,
} from '../hooks/data.js';
import { useMaterialBooking } from '../hooks/useMaterialBooking.js';
import { useAuswahl } from '../hooks/useAuswahl.js';
import { useTimer } from '../state/timer.jsx';
import { useToast } from '../state/toast.jsx';
import { deleteLeistungen } from '../lib/api.js';
import { remove as removeQueued } from '../lib/outbox.js';
import { WartendeEintraege } from '../state/outbox.jsx';
import { istGesperrt, jahrKw } from '../lib/wochen.js';
import { load, save } from '../lib/storage.js';
import { istCode, sucheArtikel, suchindex } from '../lib/artikelsuche.js';
import { clock, dateTimeLabel, dayLabel, hours, initials, isoDate, num } from '../lib/format.js';
import { ArtikelBild, artikelArt, artikelTint } from '../components/ArtikelBild.jsx';
import { Icon, PlayIcon, StopIcon } from '../components/Icon.jsx';
import { NachtragSheet } from '../sheets/NachtragSheet.jsx';
import { MengeSheet } from '../sheets/MengeSheet.jsx';
import { LeistungSheet } from '../sheets/LeistungSheet.jsx';
import { KopierSheet } from '../sheets/KopierSheet.jsx';
import { ScanSheet, canScan } from '../sheets/ScanSheet.jsx';
import { Sheet } from '../components/Sheet.jsx';
import { Stand } from '../components/Stand.jsx';
import { useFunktionen } from '../hooks/useFunktionen.js';

const TAETIGKEITEN = ['Installation', 'Montage', 'Demontage', 'Service', 'Fahrt', 'Besprechung'];
const QUICK = [0.5, 1, 2, 4, 8];

export function Capture() {
  const { hash } = useLocation();
  const materialRef = useRef(null);
  useEffect(() => {
    if (hash === '#material') materialRef.current?.querySelector('input')?.focus({ preventScroll: false });
  }, [hash]);
  const hat = useFunktionen();

  return (
    <div className="cap">
      {hat('zeit') && <TimeCard />}
      {hat('material') && <section className="cb" ref={materialRef} id="material"><MaterialPanel /></section>}
      <section className="cc stack" style={{ gap: 12 }}>
        <AuftragStand zeit={hat('zeit')} material={hat('material')} />
        {hat('material') && <BookedMaterial />}
        {hat('zeit') && <RecentTime />}
      </section>
    </div>
  );
}

/** Stand von gebuchter Zeit und Material (aus Exxas) mit «Aktualisieren». */
function AuftragStand({ zeit, material }) {
  const { nr } = useAuftragCtx();
  const leistungen = useLeistungen(nr, { laden: zeit });
  const mat = useMaterial(nr, { laden: material });
  return <Stand abfragen={[zeit && leistungen, material && mat].filter(Boolean)} />;
}

// ─── Zeit ─────────────────────────────────────────────────────────────────────

function TimeCard() {
  const { nr, auftrag } = useAuftragCtx();
  const hat = useFunktionen();
  const dieseWoche = useMeineStunden(isoDate(), { laden: hat('meineStunden') }); // Exxas-Status: eingereicht = gesperrt
  const timer = useTimer();
  const me = useMe();
  const arten = useLeistungsarten();
  const leistungen = useLeistungen(nr);
  const create = useCreateLeistung(nr);
  const toast = useToast();
  const [sheet, setSheet] = useState(null);

  const runningHere = timer.running && timer.nummer === nr;
  const elsewhere = timer.running && !runningHere;

  // Einstellungen: laufende Zeit > zuletzt verwendet > Standard. Gebucht wird immer auf mich (Exxas-Login).
  const [beschreibung, setBeschreibung] = useState(() => (runningHere ? timer.beschreibung : load('lastTaetigkeit', 'Installation')));
  const [leistungsartId, setLeistungsartId] = useState(() => (runningHere ? timer.leistungsartId : load('leistungsart', null)));
  const ready = !!me.data && !!leistungsartId;

  useEffect(() => { if (runningHere) timer.update({ beschreibung, leistungsartId }); }, [beschreibung, leistungsartId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { save('lastTaetigkeit', beschreibung); }, [beschreibung]);
  // Leistungsarten kommen aus Exxas: ungültige oder fehlende Auswahl auf einen gültigen Eintrag setzen
  useEffect(() => {
    const ok = gueltigeLeistungsart(arten.data, leistungsartId);
    if (ok !== leistungsartId) setLeistungsartId(ok);
  }, [arten.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (leistungsartId) save('leistungsart', leistungsartId); }, [leistungsartId]);

  const todayH = (leistungen.data ?? []).filter((l) => l.meine && l.datum?.startsWith(isoDate())).reduce((s, l) => s + l.stunden, 0);

  const start = () => timer.start({ nummer: nr, titel: auftrag.titel, beschreibung, leistungsartId });

  const quickBook = async (h) => {
    try {
      const r = await create.mutateAsync({ datum: isoDate(), stunden: h, beschreibung, leistungsartId, label: `${hours(h)} ${beschreibung}` });
      if (r.queued) {
        toast({ text: `Vorgemerkt: ${hours(h)} ${beschreibung} wird automatisch gebucht, sobald Netz da ist`, long: true, undo: () => { removeQueued(r.id); toast({ text: 'Nachtrag zurückgenommen' }); } });
        return;
      }
      toast({
        text: `${hours(h)} ${beschreibung} nachgetragen`,
        undo: async () => {
          const fehler = await deleteLeistungen(nr, r.res.erstellt.map((e) => e.id));
          leistungen.refetch();
          toast(fehler.length ? { text: `Nicht zurückgenommen: ${fehler[0]}`, error: true } : { text: 'Nachtrag zurückgenommen' });
        },
      });
    } catch (err) {
      toast({ text: `Nicht gebucht: ${err.message}`, error: true });
    }
  };

  const gesperrt = istGesperrt(dieseWoche.data, jahrKw(isoDate()));
  return (
    <section className="ca card-dark">
      {gesperrt && (
        <div className="notice" role="note" style={{ background: 'rgba(255,255,255,0.1)', color: '#fff' }}>
          <Icon name="lock" size={18} stroke={2.2} />
          <span>Diese Woche ist eingereicht und in Exxas gesperrt. Zeit lässt sich erst wieder buchen, wenn du die Einreichung unter «Meine Stunden» zurückziehst.</span>
        </div>
      )}
      <div className="between">
        <span className="row on-dark-muted" style={{ gap: 8, fontSize: 13, fontWeight: 700 }}>
          <span className={`dot${timer.running ? ' live' : ''}`} />
          {runningHere ? 'Zeit läuft' : elsewhere ? `Läuft auf ${timer.nummer}` : 'Bereit'}
        </span>
        <span className="tn on-dark-muted" style={{ fontSize: 13, fontWeight: 700 }}>Ich heute {hours(todayH)}</span>
      </div>

      <div className="between">
        <span className="display-zahl" style={{ fontSize: 50, fontWeight: 760, letterSpacing: '-0.02em', lineHeight: 1 }}>{clock(runningHere ? timer.elapsedSec : 0)}</span>
        {runningHere ? (
          <button className="btn stop" style={{ width: 'var(--tap-xl)', height: 'var(--tap-xl)', borderRadius: 48, padding: 0, flexShrink: 0 }} aria-label="Zeit stoppen und buchen" onClick={timer.stop} disabled={timer.busy}><StopIcon size={34} /></button>
        ) : (
          <button className="btn" style={{ width: 'var(--tap-xl)', height: 'var(--tap-xl)', borderRadius: 48, padding: 0, flexShrink: 0, background: 'var(--live)', color: 'var(--ink)' }} aria-label="Zeit starten" onClick={start} disabled={timer.busy || elsewhere || !ready}><PlayIcon size={40} /></button>
        )}
      </div>

      {elsewhere && (
        <button className="btn ghost" onClick={() => timer.switchTo({ nummer: nr, titel: auftrag.titel })} disabled={timer.busy}>
          Auf {nr} wechseln · bisherige Zeit wird gebucht
        </button>
      )}

      <label className="stack">
        <span className="vh">Leistungsart</span>
        <select className="input" value={leistungsartId ?? ''} onChange={(e) => setLeistungsartId(Number(e.target.value))} style={{ height: 'var(--tap)', background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.28)' }}>
          {(arten.data ?? []).map((a) => <option key={a.id} value={a.id} style={{ color: '#10201B' }}>{a.titel}</option>)}
        </select>
      </label>

      <div className="wrapchips" role="group" aria-label="Tätigkeit">
        {TAETIGKEITEN.map((t) => (
          <button key={t} className={`chip-dark${beschreibung === t ? ' on' : ''}`} aria-pressed={beschreibung === t} onClick={() => setBeschreibung(t)}>{t}</button>
        ))}
      </div>

      <div style={{ height: 1, background: 'rgba(255,255,255,0.12)' }} />
      <div className="stack" style={{ gap: 8 }}>
        <span className="on-dark-muted" style={{ fontSize: 13, fontWeight: 700 }}>Nachtragen mit einem Tipp (heute)</span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 6 }}>
          {QUICK.map((h) => (
            <button key={h} className="chip-dark tn" style={{ height: 'var(--tap-lg)', borderRadius: 14, padding: 0, fontSize: 16 }} aria-label={`${hours(h)} ${beschreibung} nachtragen`} onClick={() => quickBook(h)} disabled={create.isPending || !ready}>
              +{hours(h).replace(' h', '')}
            </button>
          ))}
          <button className="chip-dark" style={{ height: 'var(--tap-lg)', borderRadius: 14, padding: 0, fontSize: 16 }} onClick={() => setSheet('nachtrag')} aria-label="Andere Dauer oder anderes Datum">Mehr</button>
        </div>
      </div>

      {sheet === 'nachtrag' && (
        <NachtragSheet
          nr={nr}
          defaults={{ beschreibung, leistungsartId }}
          onClose={() => setSheet(null)}
        />
      )}
    </section>
  );
}

function RecentTime() {
  const { nr } = useAuftragCtx();
  const leistungen = useLeistungen(nr);
  const [edit, setEdit] = useState(null);
  const [kopie, setKopie] = useState(null); // { art: 'eintrag', eintrag } | { art: 'tag', datum }
  const groups = useMemo(() => {
    const map = new Map();
    for (const l of (leistungen.data ?? []).slice(0, 30)) {
      const d = (l.datum ?? '').slice(0, 10);
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(l);
    }
    return [...map.entries()].slice(0, 4);
  }, [leistungen.data]);

  return (
    <div className="card list">
      <h2 className="h2" style={{ margin: '10px 0 4px' }}>Zeit auf diesem Auftrag</h2>
      <WartendeEintraege nr={nr} kind="leistung" />
      {leistungen.isLoading && <div className="skeleton" style={{ height: 80, margin: '8px 0' }} />}
      {!leistungen.isLoading && groups.length === 0 && <p className="sub" style={{ padding: '6px 0 10px' }}>Noch keine Zeit erfasst.</p>}
      {groups.map(([d, list]) => (
        <div key={d}>
          <div className="between listrow" style={{ paddingBottom: 4 }}>
            <span className="eyebrow">{dayLabel(d)}</span>
            <span className="row" style={{ gap: 14 }}>
              <span className="tn sub" style={{ fontWeight: 700 }}>{hours(list.reduce((s, l) => s + l.stunden, 0))}</span>
              {list.some((l) => l.meine) && (
                <button type="button" className="linkbtn" onClick={() => setKopie({ art: 'tag', datum: d })} aria-label={`Meine Zeit vom ${dayLabel(d)} kopieren`}>Kopieren</button>
              )}
            </span>
          </div>
          {list.map((l) => (
            <button key={l.id} className="row plainrow" style={{ padding: '6px 0' }} onClick={() => setEdit(l)}
              aria-label={`${l.beschreibung || 'Arbeitszeit'}, ${l.mitarbeiter}, ${hours(l.stunden)}${l.gesperrt ? ', gesperrt' : l.meine ? ', bearbeiten' : ''}`}>
              <span className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>{initials(l.mitarbeiter)}</span>
              <span className="stack" style={{ flex: 1, gap: 0 }}>
                <span className="ellipsis" style={{ fontSize: 14, fontWeight: 700 }}>{l.beschreibung || l.leistungsart || 'Arbeitszeit'}</span>
                <span className="ellipsis sub" style={{ fontSize: 12 }}>{l.mitarbeiter}{l.leistungsart ? ` · ${l.leistungsart}` : ''}</span>
              </span>
              {l.gesperrt && <span className="pill grey" title="Rapportiert oder abgerechnet – nicht mehr änderbar"><Icon name="lock" size={12} stroke={2.4} />Gesperrt</span>}
              <span className="tn" style={{ fontSize: 14, fontWeight: 800 }}>{hours(l.stunden)}</span>
            </button>
          ))}
        </div>
      ))}
      {edit && <LeistungSheet nr={nr} leistung={edit} onClose={() => setEdit(null)} onKopieren={(l) => { setEdit(null); setKopie({ art: 'eintrag', eintrag: l }); }} />}
      {kopie && <KopierSheet nr={nr} quelle={kopie} onClose={() => setKopie(null)} />}
    </div>
  );
}

// ─── Material ─────────────────────────────────────────────────────────────────

function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

/** So viele Favoriten erscheinen sofort, der Rest über «Alle Favoriten anzeigen». */
const FAVORITEN_START = 20;

const stepOf = (einheit) => (/^m$|meter/i.test(einheit ?? '') ? 5 : 1);

function MaterialPanel() {
  const { nr } = useAuftragCtx();
  const [q, setQ] = useState('');
  const [gruppe, setGruppe] = useState('Alle');
  const [alleFavoriten, setAlleFavoriten] = useState(false);
  const term = useDeferredValue(q.trim()); // lokal: sofort beim Tippen
  const serverTerm = useDebounced(q.trim());
  const katalog = useArtikelKatalog();
  const aktualisieren = useKatalogAktualisieren();
  const index = useMemo(() => (katalog.data ? suchindex(katalog.data.artikel) : null), [katalog.data]);
  const lokal = useMemo(() => (index && term.length >= 2 ? sucheArtikel(index, term) : []), [index, term]);
  // Exxas direkt fragen nur, wenn der Katalog fehlt oder ein Barcode nicht im Katalog steht (EAN evtl. in anderem Feld)
  const serverNoetig = serverTerm.length >= 2 && (!index || (istCode(serverTerm) && !lokal.length) || (!katalog.data.vollstaendig && !lokal.length));
  const suche = useArtikelSuche(serverNoetig ? serverTerm : '');
  const favoriten = useFavoriten();
  const material = useMaterial(nr);
  const { qtyOf } = useMaterialBooking(nr);
  const auswahl = useAuswahl(nr);
  const toast = useToast();
  const [menge, setMenge] = useState(null);
  const [ansehen, setAnsehen] = useState(false);
  const [scan, setScan] = useState(false);
  const scanOk = useMemo(canScan, []);
  const onCode = useCallback((code) => { setScan(false); setGruppe('Alle'); setQ(code); }, []);

  const positionen = material.data ?? [];
  const posByArtikel = new Map(positionen.map((p) => [p.artikelId, p]));

  // Ohne Suche: bereits gebuchte Artikel + Favoriten. Mit Suche: Treffer aus den Stammdaten.
  const list = useMemo(() => {
    if (term.length >= 2) return lokal.length || !serverNoetig ? lokal : (suche.data ?? []);
    const booked = positionen
      .filter((p) => p.artikelId && !p.freitext)
      .map((p) => ({ id: p.artikelId, titel: p.titel, nummer: p.bezeichnung, einheit: p.einheit, bildUrl: `/api/artikel/${encodeURIComponent(p.artikelId)}/bild` }));
    const favListe = favoriten.data?.artikel ?? [];
    const fav = favListe.filter((a) => !posByArtikel.has(a.id));
    const sichtbar = alleFavoriten ? fav : fav.slice(0, FAVORITEN_START);
    // Ausgewählte Artikel (z. B. aus einer Suche) bleiben sichtbar, auch wenn sie weder gebucht noch Favorit sind
    const gezeigt = new Set([...booked.map((b) => b.id), ...sichtbar.map((a) => a.id)]);
    const gewaehlt = auswahl.items
      .filter((i) => i.artikelId && !gezeigt.has(i.artikelId))
      .map((i) => ({ id: i.artikelId, titel: i.titel, nummer: i.nummer, einheit: i.einheit, bildUrl: i.bildUrl }));
    return [...gewaehlt, ...booked.map((b) => favListe.find((f) => f.id === b.id) ?? b), ...sichtbar];
  }, [term, lokal, serverNoetig, suche.data, favoriten.data, positionen, alleFavoriten, auswahl.items]); // eslint-disable-line react-hooks/exhaustive-deps

  const speichern = async () => {
    try {
      const r = await auswahl.speichern();
      if (r.vorgemerkt) toast({ text: 'Kein Netz oder Exxas gerade ausgelastet: Auswahl ist vorgemerkt und wird automatisch in Exxas gebucht', long: true });
      else if (r.fehler) { toast({ text: `${r.gebucht} gebucht, ${r.fehler} nicht – siehe Auswahl`, error: true }); setAnsehen(true); }
      else if (r.gebucht) { toast({ text: `${r.gebucht} ${r.gebucht === 1 ? 'Position' : 'Positionen'} in Exxas gebucht` }); setAnsehen(false); }
    } catch (err) {
      toast({ text: `Nicht gebucht: ${err.message}`, error: true });
    }
  };
  const anzahlFehler = auswahl.items.filter((i) => i.fehler).length;
  const favWeitere = term.length < 2 && !alleFavoriten
    ? Math.max(0, (favoriten.data?.artikel ?? []).filter((a) => !posByArtikel.has(a.id)).length - FAVORITEN_START)
    : 0;
  const fehlend = favoriten.data?.fehlend ?? [];

  const gruppen = useMemo(() => ['Alle', ...new Set(list.map((a) => a.gruppe).filter(Boolean))], [list]);
  const shown = gruppe === 'Alle' ? list : list.filter((a) => a.gruppe === gruppe);
  const katalogLaedt = !katalog.data && katalog.isFetching;
  const loading = term.length >= 2
    ? (katalogLaedt && !serverNoetig) || (serverNoetig && (suche.isLoading || q.trim() !== serverTerm))
    : favoriten.isLoading || material.isLoading;

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="sticky">
        <div className="between" style={{ alignItems: 'baseline' }}>
          <h2 style={{ fontSize: 19, fontWeight: 800 }}>Material</h2>
          <span className="sub" style={{ fontSize: 12, fontWeight: 600 }}>Bild antippen = Menge wählen</span>
        </div>
        <label className="search">
          <Icon name="search" stroke={2.2} />
          <span className="vh">Material suchen</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Artikel, Nummer oder Stichwort, z. B. fi, kabel 2.5" autoComplete="off" enterKeyHint="search" />
          {q && <button className="iconbtn sm" aria-label="Suche leeren" onClick={() => setQ('')}><Icon name="x" size={16} stroke={2.4} /></button>}
          {scanOk && !q && <button className="iconbtn sm" aria-label="Barcode scannen" onClick={() => setScan(true)}><Icon name="barcode" size={18} stroke={2.2} /></button>}
        </label>
        {gruppen.length > 2 && (
          <div className="chips" role="group" aria-label="Artikelgruppe">
            {gruppen.map((g) => <button key={g} className={`chip${gruppe === g ? ' on' : ''}`} aria-pressed={gruppe === g} onClick={() => setGruppe(g)}>{g}</button>)}
          </div>
        )}
        {term.length < 2 && (
          <span className="sub" style={{ fontSize: 12 }}>
            Gebuchtes und Favoriten. Suchen in {katalog.data ? `${num(katalog.data.artikel.length)} Artikeln` : 'allen Artikeln'} aus den Stammdaten
            {katalog.data && <> · Stand aus Exxas {dateTimeLabel(katalog.data.stand ?? new Date(katalog.dataUpdatedAt).toISOString())} · <button className="linkbtn" style={{ fontSize: 12, padding: 0 }} onClick={() => aktualisieren.mutate()} disabled={aktualisieren.isPending} title="Artikel neu aus Exxas holen (z. B. nach Änderungen in Exxas)">{aktualisieren.isPending ? 'lädt …' : 'Aktualisieren'}</button></>}
            {katalogLaedt && ' · Artikelstamm wird geladen …'}
          </span>
        )}
      </div>

      {loading && <div className="mgrid">{[0, 1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ height: 210 }} />)}</div>}
      {(katalog.isError || aktualisieren.isError) && (
        <div className="notice err" role="alert">
          <Icon name="warn" size={18} />
          <span style={{ flex: 1 }}>
            Artikelstamm konnte nicht {katalog.data ? 'aktualisiert' : 'geladen'} werden: {(aktualisieren.error ?? katalog.error).message}
            {katalog.data ? ' Gesucht wird im gespeicherten Stand.' : ' Die Suche fragt Exxas direkt.'}
          </span>
          <button className="linkbtn" onClick={() => (katalog.data ? aktualisieren.mutate() : katalog.refetch())}>Erneut</button>
        </div>
      )}
      {suche.isError && serverNoetig && term.length >= 2 && <div className="empty" role="alert">{suche.error.message}</div>}

      {!loading && (
        <div className="mgrid">
          {shown.map((a) => {
            const pos = posByArtikel.get(a.id);
            const gebucht = qtyOf(a.id, pos?.menge ?? 0);
            const sel = auswahl.mengeVon(a.id);
            const step = stepOf(a.einheit);
            const oeffnen = () => setMenge({ artikel: a, qty: sel, gebucht });
            return (
              <div key={a.id} className={`mcard${sel > 0 ? ' sel' : ''}`}>
                <button className="mimg" style={{ background: artikelTint(a) }} aria-label={`${a.titel}: Menge wählen`} aria-pressed={sel > 0} onClick={oeffnen} data-kind={artikelArt(a.titel)}>
                  <ArtikelBild artikel={a} />
                  {sel > 0 && <span className="msel tn"><Icon name="check" size={14} stroke={3} />{num(sel)} {a.einheit}</span>}
                </button>
                <div className="stack" style={{ padding: '10px 10px 10px 12px', gap: 8, flex: 1 }}>
                  <span className="stack" style={{ gap: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.25 }}>{a.titel}</span>
                    {a.nummer && <span className="sub" style={{ fontSize: 12 }}>Nr. {a.nummer}</span>}
                    {gebucht > 0 && <span className="pill ok" style={{ alignSelf: 'flex-start', marginTop: 2 }}>auf Auftrag: {num(gebucht)} {a.einheit}</span>}
                  </span>
                  <div className="mstepper">
                    <button aria-label={`${a.titel}: ${step} ${a.einheit} weniger auswählen`} onClick={() => auswahl.setArtikel(a, Math.max(0, sel - step))} disabled={sel <= 0}><Icon name="minus" size={16} stroke={2.6} /></button>
                    <button className="q tn" aria-label={`${a.titel}: Menge eingeben`} onClick={oeffnen}>
                      {sel > 0 ? num(sel) : '–'} <span className="sub" style={{ fontSize: 12, fontWeight: 600 }}>{a.einheit}</span>
                    </button>
                    <button className="plus" aria-label={`${a.titel}: ${step} ${a.einheit} mehr auswählen`} onClick={() => auswahl.setArtikel(a, sel + step)}><Icon name="plus" size={16} stroke={2.6} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && favWeitere > 0 && gruppe === 'Alle' && (
        <button className="btn secondary block" onClick={() => setAlleFavoriten(true)}>Alle Favoriten anzeigen (+{favWeitere})</button>
      )}
      {!loading && term.length < 2 && fehlend.length > 0 && (
        <details className="sub" style={{ fontSize: 12 }}>
          <summary>{fehlend.length} {fehlend.length === 1 ? 'Favorit' : 'Favoriten'} nicht im Artikelstamm gefunden</summary>
          <p style={{ marginTop: 6, lineHeight: 1.5 }}>{fehlend.join(' · ')}</p>
        </details>
      )}
      {!loading && term.length >= 2 && shown.length === 0 && (
        <div className="empty">
          Nichts in den Stammdaten gefunden.
          <button className="btn" onClick={() => setMenge({ artikel: { titel: q.trim(), einheit: 'Stk' }, qty: 0, gebucht: 0, freitext: true })}>«{q.trim()}» als Freitext auswählen</button>
        </div>
      )}
      {!loading && term.length >= 2 && shown.length > 0 && (
        <button className="btn secondary block" onClick={() => setMenge({ artikel: { titel: q.trim(), einheit: 'Stk' }, qty: 0, gebucht: 0, freitext: true })}>
          Nicht dabei? «{q.trim()}» als Freitext auswählen
        </button>
      )}
      {!loading && term.length < 2 && shown.length === 0 && (
        <div className="empty">Noch keine Favoriten hinterlegt. Suche oben nach Artikeln aus den Exxas-Stammdaten.</div>
      )}

      {scan && <ScanSheet onCode={onCode} onClose={() => setScan(false)} />}
      {auswahl.items.length > 0 && (
        <div className="savebar" role="region" aria-label="Material-Auswahl">
          {anzahlFehler > 0 && <div className="notice err" role="alert"><Icon name="warn" size={18} />{anzahlFehler} {anzahlFehler === 1 ? 'Position wurde' : 'Positionen wurden'} nicht gebucht – Details unter «Ansehen».</div>}
          <div className="row">
            <button className="plainbtn" style={{ flex: 1, minWidth: 0 }} onClick={() => setAnsehen(true)}>
              <span style={{ fontSize: 15, fontWeight: 800 }}>{auswahl.items.length} ausgewählt</span>
              <span className="sub" style={{ fontSize: 12, fontWeight: 700, textDecoration: 'underline' }}>Ansehen &amp; ändern</span>
            </button>
            <button className="btn" onClick={speichern} disabled={auswahl.busy}>{auswahl.busy ? 'Bucht …' : 'Speichern'}</button>
          </div>
        </div>
      )}

      {scan && <ScanSheet onCode={onCode} onClose={() => setScan(false)} />}
      {menge && (
        <MengeSheet
          artikel={menge.artikel}
          current={menge.qty}
          gebucht={menge.gebucht}
          modus="auswahl"
          onClose={() => setMenge(null)}
          onSet={(value) => {
            if (menge.freitext) { if (value > 0) { auswahl.addFreitext(menge.artikel.titel, value); setQ(''); } }
            else auswahl.setArtikel(menge.artikel, value);
            setMenge(null);
          }}
        />
      )}
      {ansehen && <AuswahlSheet auswahl={auswahl} onSpeichern={speichern} onClose={() => setAnsehen(false)} />}
    </div>
  );
}

/** Auswahl prüfen: Mengen ändern, entfernen, dann speichern. */
function AuswahlSheet({ auswahl, onSpeichern, onClose }) {
  const { items } = auswahl;
  useEffect(() => { if (!items.length) onClose(); }, [items.length, onClose]);
  return (
    <Sheet title={`Auswahl (${items.length})`} onClose={onClose}>
      <p className="sub">Wird mit «Speichern» als Artikel in Exxas gebucht. Freitext nur, wo so markiert.</p>
      <div className="stack" style={{ gap: 8 }}>
        {items.map((i) => (
          <div key={i.key} className="row" style={{ background: 'var(--bg)', borderRadius: 14, padding: 8, alignItems: 'center' }}>
            <span className="athumb" style={{ background: artikelTint(i) }}>{i.freitext ? <Icon name="edit" size={18} /> : <ArtikelBild artikel={{ ...i, id: i.artikelId }} size={36} />}</span>
            <span className="stack" style={{ flex: 1, gap: 0, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.25 }}>{i.titel}</span>
              <span className="sub" style={{ fontSize: 12 }}>{i.freitext ? 'Freitext' : `Nr. ${i.nummer}`}</span>
              {i.fehler && <span className="error" style={{ fontSize: 12 }}>Nicht gebucht: {i.fehler}</span>}
            </span>
            <MengeFeld wert={i.menge} einheit={i.einheit} label={`${i.titel}: Menge`} onChange={(m) => auswahl.setMenge(i.key, m)} />
            <button className="iconbtn white sm" aria-label={`${i.titel} aus der Auswahl entfernen`} onClick={() => auswahl.setMenge(i.key, 0)}><Icon name="x" size={14} /></button>
          </div>
        ))}
      </div>
      <button className="btn lg block" onClick={onSpeichern} disabled={auswahl.busy || !items.length}>
        {auswahl.busy ? 'Bucht …' : `${items.length} ${items.length === 1 ? 'Position' : 'Positionen'} in Exxas speichern`}
      </button>
      <button className="btn secondary block" onClick={() => { auswahl.leeren(); onClose(); }} disabled={auswahl.busy}>Auswahl verwerfen</button>
    </Sheet>
  );
}

/** Mengenfeld, das beim Tippen nicht springt (leer/«2,» erlaubt) und erst gültige Werte übernimmt. */
function MengeFeld({ wert, einheit, label, onChange }) {
  const [text, setText] = useState(String(wert));
  useEffect(() => { setText((t) => (Number(t.replace(',', '.')) === wert ? t : String(wert))); }, [wert]);
  return (
    <span className="row" style={{ gap: 4 }}>
      <input className="input tn" style={{ width: 72, height: 40, padding: '0 8px', textAlign: 'right' }} inputMode="decimal" aria-label={label} value={text}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value.replace(',', '.'));
          if (Number.isFinite(n) && n > 0 && n <= 100000) onChange(n);
        }} />
      <span className="sub" style={{ fontSize: 12, width: 28 }}>{einheit}</span>
    </span>
  );
}

function BookedMaterial() {
  const { nr } = useAuftragCtx();
  const material = useMaterial(nr);
  const { book } = useMaterialBooking(nr);
  const list = material.data ?? [];
  return (
    <div className="card list">
      <div className="between" style={{ margin: '10px 0 4px' }}>
        <h2 className="h2">Material auf {nr}</h2>
        <span className="sub" style={{ fontWeight: 700 }}>{list.length} {list.length === 1 ? 'Position' : 'Positionen'}</span>
      </div>
      <WartendeEintraege nr={nr} kind="material" />
      {material.isLoading && <div className="skeleton" style={{ height: 60, margin: '8px 0' }} />}
      {!material.isLoading && list.length === 0 && <p className="sub" style={{ padding: '6px 0 10px' }}>Noch kein Material gebucht.</p>}
      {list.map((p) => (
        <div key={p.id} className="listrow" style={{ padding: '8px 0' }}>
          <span className="ellipsis" style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>{p.titel}</span>
          <span className="tn" style={{ fontSize: 14, fontWeight: 800 }}>{num(p.menge)} {p.einheit}</span>
          {p.artikelId && !p.freitext && (
            <button className="iconbtn sm" aria-label={`${p.titel} um ${stepOf(p.einheit)} reduzieren`}
              onClick={() => book({ id: p.artikelId, titel: p.titel, nummer: p.bezeichnung, einheit: p.einheit }, -Math.min(stepOf(p.einheit), p.menge))}>
              <Icon name="minus" size={14} stroke={2.6} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
